@file:Suppress("DEPRECATION")
package org.example.newportfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.style.ComponentStyle
import com.varabyte.kobweb.silk.components.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.components.style.hover
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.overlay
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.browser.window
import org.example.newportfolio.models.Product
import org.example.newportfolio.theme.brand
import org.example.newportfolio.theme.fonts.*
import org.jetbrains.compose.web.css.*

// ──── Feature Card Style ─────────────────────────────────────────────────────
val FeatureCardStyle by ComponentStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .padding(28.px)
            .borderRadius(20.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.04f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f))
            .transition(
                CSSTransition("transform", 0.25.s, TransitionTimingFunction.EaseOut),
                CSSTransition("border-color", 0.25.s, TransitionTimingFunction.EaseOut),
                CSSTransition("box-shadow", 0.25.s, TransitionTimingFunction.EaseOut)
            )
    }
    hover {
        Modifier
            .transform { translateY((-4).px) }
            .border(1.px, LineStyle.Solid, palette.brand.primary.toRgb().copyf(alpha = 0.4f))
            .boxShadow(0.px, 12.px, 30.px, 0.px, palette.brand.primary.toRgb().copyf(alpha = 0.15f))
    }
}

// ──── Back Button Style ──────────────────────────────────────────────────────
val BackButtonStyle by ComponentStyle {
    base {
        Modifier
            .padding(leftRight = 20.px, topBottom = 10.px)
            .borderRadius(12.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.08f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.15f))
            .cursor(Cursor.Pointer)
            .color(Colors.White)
            .fontSize(14.px)
            .transition(CSSTransition("all", 0.2.s, TransitionTimingFunction.EaseInOut))
    }
    hover {
        Modifier
            .backgroundColor(Color.rgba(255, 255, 255, 0.15f))
            .transform { translateX((-4).px) }
    }
}

// ──── Status Badge Style ─────────────────────────────────────────────────────
val StatusBadgeStyle by ComponentStyle {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .padding(leftRight = 16.px, topBottom = 6.px)
            .borderRadius(20.px)
            .backgroundColor(palette.brand.primary.toRgb().copyf(alpha = 0.15f))
            .border(1.px, LineStyle.Solid, palette.brand.primary.toRgb().copyf(alpha = 0.4f))
            .fontSize(13.px)
            .color(Colors.White)
    }
}

// ──── Tech Badge Style ───────────────────────────────────────────────────────
val ProductTechBadgeStyle by ComponentStyle {
    base {
        Modifier
            .padding(leftRight = 14.px, topBottom = 6.px)
            .borderRadius(20.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.06f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.12f))
            .fontSize(13.px)
            .color(Color.rgba(255, 255, 255, 0.8f))
    }
}


@Page("/product/{name}")
@Composable
fun ProductPage() {
    val ctx = rememberPageContext()
    val bp = rememberBreakpoint()
    val palette = ColorMode.current.toPalette()
    val productName = ctx.route.params["name"] ?: ""

    val product = Product.entries.find { it.name.lowercase() == productName.lowercase() }

    if (product == null) {
        // Redirect home if product not found. REPLACE so the bad URL doesn't
        // stay in history as a back-button trap.
        LaunchedEffect(Unit) {
            ctx.router.navigateTo("/", UpdateHistoryMode.REPLACE)
        }
        return
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .backgroundColor(Color.rgb(14, 13, 22))
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(1200.px)
                .margin(leftRight = autoLength)
                .padding(
                    left = if (bp >= Breakpoint.MD) 60.px else 20.px,
                    right = if (bp >= Breakpoint.MD) 60.px else 20.px,
                    top = 40.px,
                    bottom = 80.px
                )
        ) {
            // ─── Back button ─────────────────────────────────────────────
            Box(
                modifier = BackButtonStyle.toModifier()
                    .margin(bottom = 40.px)
                    .onClick { ctx.router.navigateTo("/") }
            ) {
                SpanText("← Back to Portfolio")
            }

            // ─── Hero Section ────────────────────────────────────────────
            Column(
                modifier = Modifier.fillMaxWidth().margin(bottom = 60.px)
            ) {
                // Status badge
                Box(modifier = StatusBadgeStyle.toModifier().margin(bottom = 16.px)) {
                    SpanText(product.status)
                }

                // Product name
                SpanText(
                    text = product.productName,
                    modifier = TextStyle.toModifier(DisplayTextStyle)
                        .fontSize(if (bp >= Breakpoint.MD) 52.px else 32.px)
                        .color(Colors.White)
                        .margin(bottom = 12.px)
                )

                // Tagline
                SpanText(
                    text = product.tagline,
                    modifier = TextStyle.toModifier(SubDisplayTextStyle)
                        .fontSize(if (bp >= Breakpoint.MD) 22.px else 18.px)
                        .color(palette.brand.primary)
                        .margin(bottom = 24.px)
                )

                // Accent divider
                Box(
                    modifier = Modifier
                        .width(80.px)
                        .height(3.px)
                        .borderRadius(2.px)
                        .backgroundColor(palette.brand.primary)
                        .margin(bottom = 28.px)
                )

                // Detailed description
                SpanText(
                    text = product.detailedDescription,
                    modifier = TextStyle.toModifier(BodyLargeTextStyle)
                        .lineHeight(1.8)
                        .color(Color.rgba(255, 255, 255, 0.75f))
                        .maxWidth(if (bp >= Breakpoint.MD) 800.px else 100.percent)
                        .margin(bottom = 32.px)
                )

                // Product images
                if (product.detailImages.isNotEmpty()) {
                    SimpleGrid(
                        numColumns = numColumns(base = 1, md = 2),
                        modifier = Modifier.fillMaxWidth().gap(20.px)
                    ) {
                        product.detailImages.forEach { imageSrc ->
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(if (bp >= Breakpoint.MD) 400.px else 240.px)
                                    .borderRadius(20.px)
                                    .backgroundColor(Color.rgb(20, 20, 30))
                                    .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.1f))
                                    .overflow(Overflow.Hidden),
                                contentAlignment = Alignment.Center
                            ) {
                                Image(
                                    src = "/$imageSrc",
                                    alt = "${product.productName} app screenshot",
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .objectFit(ObjectFit.Cover)
                                )
                            }
                        }
                    }
                } else {
                    // Product image placeholder (white card)
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(if (bp >= Breakpoint.MD) 400.px else 240.px)
                            .borderRadius(20.px)
                            .backgroundColor(Colors.White)
                            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.1f))
                            .overflow(Overflow.Hidden),
                        contentAlignment = Alignment.Center
                    ) {
                        // Show the product image if available, otherwise white placeholder
                        Image(
                            src = "/${product.imageRes}",
                            alt = "${product.productName} app screenshot",
                            modifier = Modifier
                                .fillMaxSize()
                                .objectFit(ObjectFit.Contain)
                                .padding(20.px)
                        )
                    }
                }
            }

            // ─── Tech Stack Section ──────────────────────────────────────
            Column(modifier = Modifier.fillMaxWidth().margin(bottom = 60.px)) {
                SpanText(
                    text = "TECH STACK",
                    modifier = TextStyle.toModifier(LabelLargeTextStyle)
                        .color(Color.rgba(255, 255, 255, 0.45f))
                        .fontSize(13.px)
                        .letterSpacing(2.px)
                        .margin(bottom = 16.px)
                )
                Row(
                    modifier = Modifier.fillMaxWidth().gap(10.px).flexWrap(FlexWrap.Wrap)
                ) {
                    product.techStack.forEach { tech ->
                        Box(modifier = ProductTechBadgeStyle.toModifier()) {
                            SpanText(tech)
                        }
                    }
                }
            }

            // ─── Features Section ────────────────────────────────────────
            Column(modifier = Modifier.fillMaxWidth().margin(bottom = 60.px)) {
                SpanText(
                    text = "FEATURES",
                    modifier = TextStyle.toModifier(LabelLargeTextStyle)
                        .color(Color.rgba(255, 255, 255, 0.45f))
                        .fontSize(13.px)
                        .letterSpacing(2.px)
                        .margin(bottom = 8.px)
                )
                SpanText(
                    text = product.tagline,
                    modifier = TextStyle.toModifier(DisplayTextStyle)
                        .fontSize(if (bp >= Breakpoint.MD) 32.px else 24.px)
                        .color(Colors.White)
                        .margin(bottom = 40.px)
                )

                SimpleGrid(
                    numColumns = numColumns(base = 1, sm = 1, md = 2, lg = 2, xl = 2)
                ) {
                    product.features.forEach { feature ->
                        Box(modifier = FeatureCardStyle.toModifier().margin(8.px)) {
                            Column(modifier = Modifier.fillMaxWidth()) {
                                // Icon
                                SpanText(
                                    text = feature.icon,
                                    modifier = Modifier
                                        .fontSize(36.px)
                                        .margin(bottom = 16.px)
                                )
                                // Title
                                SpanText(
                                    text = feature.title,
                                    modifier = TextStyle.toModifier(LabelLargeTextStyle)
                                        .color(Colors.White)
                                        .fontSize(18.px)
                                        .margin(bottom = 10.px)
                                )
                                // Description
                                SpanText(
                                    text = feature.description,
                                    modifier = TextStyle.toModifier(BodyLargeTextStyle)
                                        .lineHeight(1.7)
                                        .color(Color.rgba(255, 255, 255, 0.65f))
                                        .fontSize(14.px)
                                )
                            }
                        }
                    }
                }
            }

            // ─── CTA Section ─────────────────────────────────────────────
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(40.px)
                    .borderRadius(24.px)
                    .backgroundColor(Color.rgba(255, 255, 255, 0.03f))
                    .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f)),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    text = "Interested in ${product.productName}?",
                    modifier = TextStyle.toModifier(DisplayTextStyle)
                        .fontSize(if (bp >= Breakpoint.MD) 28.px else 22.px)
                        .color(Colors.White)
                        .margin(bottom = 12.px)
                        .textAlign(TextAlign.Center)
                )
                SpanText(
                    text = "Get early access or learn more about the project.",
                    modifier = TextStyle.toModifier(BodyLargeTextStyle)
                        .color(Color.rgba(255, 255, 255, 0.6f))
                        .margin(bottom = 28.px)
                        .textAlign(TextAlign.Center)
                )
                Box(
                    modifier = Modifier
                        .padding(leftRight = 32.px, topBottom = 14.px)
                        .borderRadius(14.px)
                        .backgroundColor(palette.brand.primary)
                        .cursor(Cursor.Pointer)
                        .color(Colors.White)
                        .fontSize(16.px)
                        .fontWeight(FontWeight.SemiBold)
                        .onClick { window.open(product.link, "_blank") },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText("View on GitHub →")
                }
            }
        }
    }
}
